// Campaign detail page - displays campaign info and creator interactions (tasks)

import { InteractionItem } from "@/components/projects/interaction-item";
import { useCampaignArchiveState } from "@/hooks/use-campaign-archive-state";
import { recordRecentProject } from "@/hooks/use-recent-projects";
import {
  useProjectInteractionsQuery,
  type CreatorInteraction,
  type Project,
} from "@/lib/queries/projects";

import type { CampaignRouteContext } from "@/lib/types/route-context";

import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Skeleton,
} from "@repo/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo } from "react";

export const Route = createFileRoute(
  "/(org)/o/$orgSlug/p/$profileSlug/c/$campaignId/",
)({
  component: ProjectDetailContainer,
});

function ProjectDetailContainer() {
  const { campaign, programId, profile, organization } =
    Route.useRouteContext() as CampaignRouteContext;

  // Get interactions for this campaign
  const {
    data: interactionsData,
    isPending: interactionsPending,
    error: interactionsError,
  } = useProjectInteractionsQuery(organization.id, programId, profile.id);
  const { isArchived } = useCampaignArchiveState(organization.id, campaign);

  useEffect(() => {
    if (campaign) {
      recordRecentProject(organization.id, `${programId}-${profile.id}`);
    }
  }, [programId, profile.id, campaign, organization.id]);

  const interactions = useMemo(() => {
    const list = interactionsData?.interactions ?? [];
    return [...list].sort((a, b) =>
      (b.updatedDate ?? "").localeCompare(a.updatedDate ?? ""),
    );
  }, [interactionsData]);

  return (
    <ProjectDetailView
      project={campaign}
      interactions={interactions}
      isInteractionsLoading={interactionsPending}
      interactionsError={interactionsError}
      orgSlug={organization.slug}
      profileSlug={campaign.profileSlug}
      campaignId={String(programId)}
      organizationId={organization.id}
      isArchived={isArchived}
    />
  );
}

interface ProjectDetailViewProps {
  project?: Project;
  interactions: CreatorInteraction[];
  isInteractionsLoading: boolean;
  interactionsError: unknown;
  orgSlug: string;
  profileSlug: string;
  campaignId: string;
  organizationId: string;
  /** EPD-5559: tasks are read-only until the creator unarchives. */
  isArchived?: boolean;
}

export function ProjectDetailView({
  project,
  interactions,
  isInteractionsLoading,
  interactionsError,
  orgSlug,
  profileSlug,
  campaignId,
  organizationId,
  isArchived = false,
}: ProjectDetailViewProps) {
  if (!project) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-muted-foreground">
              Campaign not found. It may have been removed or you don't have
              access.
            </p>
            <Button variant="outline" className="mt-4" asChild>
              <Link to="/o/$orgSlug/projects" params={{ orgSlug }}>
                Back to Campaigns
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="px-6 pb-6 space-y-6">
      {/* Tasks / Interactions */}
      <Card>
        <CardHeader>
          <CardTitle>Tasks</CardTitle>
          <CardDescription>
            Complete these tasks to participate in this campaign
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {isInteractionsLoading ? (
            <InteractionsSkeleton />
          ) : interactionsError ? (
            <p className="p-6 text-destructive text-sm">
              Failed to load tasks. Please try again.
            </p>
          ) : interactions.length === 0 ? (
            <p className="p-6 text-muted-foreground text-sm">
              No tasks available for this campaign.
            </p>
          ) : (
            <div className="space-y-3 p-6">
              {interactions.map((interaction) => (
                <InteractionItem
                  key={interaction.id}
                  interaction={interaction}
                  readOnly={isArchived}
                  profileSlug={profileSlug}
                  campaignId={campaignId}
                  orgSlug={orgSlug}
                  organizationId={organizationId}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function InteractionsSkeleton() {
  return (
    <div className="space-y-3 p-6">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="p-3 rounded-lg border bg-card">
          <div className="flex items-start gap-3">
            <Skeleton className="h-4 w-4 mt-0.5 shrink-0 rounded-full" />
            <div className="flex-1 min-w-0 space-y-2">
              <div className="flex items-start gap-2">
                <Skeleton className="h-4 w-4 shrink-0" />
                <Skeleton className="h-4 w-48" />
              </div>
              <Skeleton className="h-3 w-full max-w-sm" />
              <Skeleton className="h-5 w-20 rounded-full" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
